/**
 * In-memory settings client for Vitest / mock Slack mode.
 * Mirrors server masking: never returns full secret values.
 */

import { ApiEnvelope } from '../../domain/types';
import {
  simulateNetwork,
  DEFAULT_CONFIG,
  isMockSourceDisconnected,
  isMockSourceRejected,
  mockRejectedCredentialMessage,
} from './mockClientUtils';
import {
  aiUsageDayKeys,
  defaultCompetenceProfile,
  defaultGmailConnectionSettings,
  isAiTaskId,
  summarizeAiUsage,
  normalizeCompetenceProfile,
  normalizeGmailAttentionRules,
  STATIC_AI_MODEL_OPTIONS,
  type CompetenceProfile,
} from '@zunelo/shared';
import {
  AiGenerationSettings,
  AiModelCatalogResponse,
  AiProviderModelCatalog,
  AiUsageSummary,
  AiUsageWindow,
  ClientConfig,
  GmailConnectionSettings,
  ISettingsClient,
  JiraConnectionSettings,
  SecretStatus,
  SlackVoiceSettings,
  UserSettingsPublic,
  UserSettingsUpdate,
} from './types';

function mask(value: string | undefined): SecretStatus {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) return { configured: false, lastFour: null };
  return {
    configured: true,
    lastFour: trimmed.length <= 4 ? trimmed : trimmed.slice(-4),
  };
}

function defaultAi(): AiGenerationSettings {
  return { provider: 'opencode-go', model: 'deepseek-v4-flash' };
}

function defaultVoice(): SlackVoiceSettings {
  return {
    language: 'es',
    tone: 'professional',
    replyLength: 'medium',
    customPrompt: '',
  };
}

function defaultJira(): JiraConnectionSettings {
  return {
    baseUrl: '',
    email: '',
    authMethod: 'none',
    oauthConnected: false,
    oauthSiteName: '',
  };
}

function normalizeBaseUrl(value: string): string {
  return value.trim().replace(/\/+$/, '');
}

/**
 * Mock model catalog. Rich by default (so every dropdown variation renders),
 * `not_configured` for a provider marked disconnected via
 * `?disconnected=<provider>`, and degraded to the static list with an error
 * message for one marked rejected via `?rejected=<provider>` — the three
 * states the Feature Mocking Protocol asks every provider mock to cover.
 */
const MOCK_LIVE_MODELS: Record<string, string[]> = {
  'opencode-go': ['deepseek-v4-flash', 'deepseek-v4-pro', 'kimi-k3', 'glm-5.3-flash'],
  gemini: ['gemini-2.5-flash', 'gemini-2.5-pro'],
  'openai-codex': ['gpt-5-codex', 'gpt-5'],
};

function mockProviderCatalog(
  provider: 'opencode-go' | 'gemini' | 'openai-codex',
  checkedAt: string,
): AiProviderModelCatalog {
  const staticModels = STATIC_AI_MODEL_OPTIONS[provider].map((id) => ({ id, label: id }));
  if (isMockSourceRejected(provider)) {
    return {
      provider,
      status: 'error',
      source: 'static',
      models: staticModels,
      checkedAt,
      message: 'HTTP 401',
    };
  }
  if (isMockSourceDisconnected(provider)) {
    return {
      provider,
      status: 'not_configured',
      source: 'static',
      models: staticModels,
      checkedAt,
      message: 'Sin credenciales configuradas.',
    };
  }
  return {
    provider,
    status: 'ok',
    source: 'live',
    models: MOCK_LIVE_MODELS[provider].map((id) => ({ id, label: id })),
    checkedAt,
  };
}

/**
 * Deterministic usage fixture: a few days of two providers and three tasks,
 * including one failing day, so every column of the Uso table renders. The
 * `?disconnected=usage` selector serves the empty state instead.
 */
function mockUsageDays(days: AiUsageWindow) {
  const keys = aiUsageDayKeys(new Date(), days);
  const recent = keys.slice(-5);
  return keys.map((day) => {
    const position = recent.indexOf(day);
    if (position < 0) return { day, entries: [] };
    const lastAt = `${day}T12:00:00.000Z`;
    return {
      day,
      entries: [
        {
          provider: 'opencode-go' as const,
          model: 'deepseek-v4-flash',
          task: 'threadSummary' as const,
          attempt: 'primary' as const,
          effort: 'low' as const,
          calls: 6 + position,
          failures: 0,
          promptTokens: 4200 + position * 300,
          completionTokens: 640 + position * 40,
          totalTokens: 4840 + position * 340,
          latencyMsTotal: (6 + position) * 1800,
          lastAt,
        },
        {
          provider: 'opencode-go' as const,
          model: 'deepseek-v4-pro',
          task: 'pullRequestReview' as const,
          attempt: 'primary' as const,
          effort: 'medium' as const,
          calls: position === 0 ? 2 : 1,
          // The oldest day of the window is the one that failed.
          failures: position === 0 ? 2 : 0,
          promptTokens: position === 0 ? 0 : 24_000,
          completionTokens: position === 0 ? 0 : 1_800,
          totalTokens: position === 0 ? 0 : 25_800,
          latencyMsTotal: position === 0 ? 2_400 : 14_500,
          lastAt,
          ...(position === 0 ? { lastErrorStatus: 400 } : {}),
        },
        {
          provider: 'gemini' as const,
          model: 'gemini-2.5-flash',
          task: 'threadAsk' as const,
          // The fallback row: this is how a user sees that the route they
          // chose was not the one that answered.
          attempt: 'fallback' as const,
          effort: 'low' as const,
          calls: 2,
          failures: position === 1 ? 1 : 0,
          promptTokens: 1_500,
          completionTokens: 300,
          totalTokens: 1_800,
          latencyMsTotal: 3_600,
          lastAt,
          ...(position === 1 ? { lastErrorStatus: 429 } : {}),
        },
      ],
    };
  });
}

export class MockSettingsClient implements ISettingsClient {
  private slackUserToken?: string;
  private opencodeGoApiKey?: string;
  // Gemini / OpenAI keys were never stored here, so demo mode could not reach
  // the "this AI provider is connected" state the per-task routing UI needs.
  private geminiApiKey?: string;
  private openAiApiKey?: string;
  private jiraApiToken?: string;
  private githubPersonalAccessToken?: string;
  private ai: AiGenerationSettings = defaultAi();
  private slackVoice: SlackVoiceSettings = defaultVoice();
  private jira: JiraConnectionSettings = defaultJira();
  private gmail: GmailConnectionSettings = defaultGmailConnectionSettings();
  private competence: CompetenceProfile = defaultCompetenceProfile();

  constructor(private config: ClientConfig = DEFAULT_CONFIG) {
    // Dev fixture: a rejected credential implies one is stored, so seed placeholders
    // for `?rejected=<sources>` to make the Settings header show the verdict.
    if (isMockSourceRejected('slack')) this.slackUserToken = 'mock-rejected-slack-token';
    if (isMockSourceRejected('github')) this.githubPersonalAccessToken = 'mock-rejected-github-pat';
    if (isMockSourceRejected('jira')) {
      this.jiraApiToken = 'mock-rejected-jira-token';
      this.jira = {
        ...this.jira,
        baseUrl: 'https://demo.atlassian.net',
        email: 'agent@zunelo.local',
        authMethod: 'api_token',
      };
    }
  }

  private sourceSummary(provider: 'slack' | 'jira' | 'github' | 'gmail', configured: boolean) {
    const rejected = isMockSourceRejected(provider);
    return {
      provider,
      status: configured ? ('connected' as const) : ('disconnected' as const),
      accountLabel: '',
      credentialStatus: rejected ? ('rejected' as const) : configured ? ('ok' as const) : null,
      credentialCheckedAt: configured ? new Date().toISOString() : null,
      credentialDetail: rejected ? mockRejectedCredentialMessage(provider) : null,
      expiresAt: provider === 'jira' ? (this.jira.apiTokenExpiresAt ?? null) : null,
    };
  }

  private toPublic(): UserSettingsPublic {
    return {
      ai: { ...this.ai },
      providers: {
        sources: [
          this.sourceSummary('slack', Boolean(this.slackUserToken)),
          this.sourceSummary(
            'jira',
            Boolean(this.jiraApiToken && this.jira.baseUrl && this.jira.email),
          ),
          this.sourceSummary('github', Boolean(this.githubPersonalAccessToken)),
          this.sourceSummary('gmail', this.gmail.connected),
        ],
        ai: [
          {
            provider: 'openai-codex',
            status: this.openAiApiKey ? 'connected' : 'disconnected',
            accountLabel: '',
            modelLabel: 'Codex',
          },
          {
            provider: 'gemini',
            status: this.geminiApiKey ? 'connected' : 'disconnected',
            accountLabel: '',
            modelLabel: 'Gemini',
          },
          {
            provider: 'opencode-go',
            status: this.opencodeGoApiKey ? 'connected' : 'disconnected',
            accountLabel: '',
            modelLabel: 'OpenCode Go',
          },
        ],
      },
      oauth: { external: { enabled: false } },
      competence: normalizeCompetenceProfile(this.competence),
      slackVoice: { ...this.slackVoice },
      jira: { ...this.jira },
      gmail: { ...this.gmail },
      secrets: {
        slackUserToken: mask(this.slackUserToken),
        opencodeGoApiKey: mask(this.opencodeGoApiKey),
        geminiApiKey: mask(this.geminiApiKey),
        openAiApiKey: mask(this.openAiApiKey),
        jiraApiToken: mask(this.jiraApiToken),
        githubPersonalAccessToken: mask(this.githubPersonalAccessToken),
        gmailOAuth: mask(this.gmail.connected ? 'mock-gmail-token' : undefined),
      },
    };
  }

  /** Mirrors the server: task overrides merge one task at a time. */
  private applyAiPatch(patch: NonNullable<UserSettingsUpdate['ai']>): void {
    const tasks = { ...(this.ai.tasks ?? {}) };
    for (const [task, override] of Object.entries(patch.tasks ?? {})) {
      if (!isAiTaskId(task)) continue;
      // An override sent as `{}` is the explicit "back to the global route".
      if (override && Object.keys(override).length) tasks[task] = override;
      else delete tasks[task];
    }
    const effort = patch.effort === null ? undefined : (patch.effort ?? this.ai.effort);
    this.ai = {
      ...this.ai,
      ...patch,
      provider: patch.provider ?? this.ai.provider,
      model: patch.model?.trim() || this.ai.model,
      effort,
      ...(Object.keys(tasks).length ? { tasks } : { tasks: undefined }),
    };
  }

  async getSettings(): Promise<ApiEnvelope<UserSettingsPublic>> {
    return simulateNetwork(() => this.toPublic(), this.config);
  }

  async listAiModels(): Promise<ApiEnvelope<AiModelCatalogResponse>> {
    return simulateNetwork(() => {
      const checkedAt = new Date().toISOString();
      return {
        providers: (['opencode-go', 'gemini', 'openai-codex'] as const).map((provider) =>
          mockProviderCatalog(provider, checkedAt),
        ),
      };
    }, this.config);
  }

  /** One place per secret field, mirroring the server's `applySecretPatch`. */
  private applySecretPatches(patch: UserSettingsUpdate): void {
    const fields: Array<
      [
        (
          | 'slackUserToken'
          | 'opencodeGoApiKey'
          | 'geminiApiKey'
          | 'openAiApiKey'
          | 'jiraApiToken'
          | 'githubPersonalAccessToken'
        ),
        keyof UserSettingsUpdate,
        keyof UserSettingsUpdate,
      ]
    > = [
      ['slackUserToken', 'slackUserToken', 'clearSlackUserToken'],
      ['opencodeGoApiKey', 'opencodeGoApiKey', 'clearOpencodeGoApiKey'],
      ['geminiApiKey', 'geminiApiKey', 'clearGeminiApiKey'],
      ['openAiApiKey', 'openAiApiKey', 'clearOpenAiApiKey'],
      ['jiraApiToken', 'jiraApiToken', 'clearJiraApiToken'],
      ['githubPersonalAccessToken', 'githubPersonalAccessToken', 'clearGithubPersonalAccessToken'],
    ];
    for (const [field, valueKey, clearKey] of fields) {
      if (patch[clearKey]) {
        this[field] = undefined;
        continue;
      }
      const value = patch[valueKey];
      if (typeof value === 'string' && value.trim()) this[field] = value.trim();
    }
  }

  private applyVoicePatch(patch: NonNullable<UserSettingsUpdate['slackVoice']>): void {
    this.slackVoice = {
      ...this.slackVoice,
      ...patch,
      customPrompt:
        patch.customPrompt !== undefined ? patch.customPrompt.trim() : this.slackVoice.customPrompt,
    };
  }

  private applyJiraPatch(patch: NonNullable<UserSettingsUpdate['jira']>): void {
    const next: JiraConnectionSettings = { ...this.jira, ...patch };
    if (patch.baseUrl !== undefined) next.baseUrl = normalizeBaseUrl(patch.baseUrl);
    if (patch.email !== undefined) next.email = patch.email.trim();
    this.jira = next;
  }

  async getAiUsage(days: AiUsageWindow): Promise<ApiEnvelope<AiUsageSummary>> {
    // `?rejected=usage` exercises the read-failure copy in the browser; the
    // Feature Mocking Protocol asks every provider mock for all three states.
    if (isMockSourceRejected('usage')) {
      throw Object.assign(new Error('No se pudo leer el uso de IA.'), {
        code: 'AI_USAGE_HTTP_ERROR',
        status: 503,
      });
    }
    return simulateNetwork(() => {
      const usageDays = isMockSourceDisconnected('usage')
        ? aiUsageDayKeys(new Date(), days).map((day) => ({ day, entries: [] }))
        : mockUsageDays(days);
      return summarizeAiUsage(usageDays, {
        since: usageDays[0]?.day ?? '',
        until: usageDays[usageDays.length - 1]?.day ?? '',
      });
    }, this.config);
  }

  async updateSettings(patch: UserSettingsUpdate): Promise<ApiEnvelope<UserSettingsPublic>> {
    return simulateNetwork(() => {
      if (patch.competence) this.competence = normalizeCompetenceProfile(patch.competence);
      this.applySecretPatches(patch);
      if (patch.ai) this.applyAiPatch(patch.ai);
      if (patch.slackVoice) this.applyVoicePatch(patch.slackVoice);
      if (patch.jira) this.applyJiraPatch(patch.jira);
      // Saving a Jira token is what promotes the connection to api_token.
      if (typeof patch.jiraApiToken === 'string' && patch.jiraApiToken.trim()) {
        this.jira = { ...this.jira, authMethod: 'api_token' };
      }
      if (patch.gmail?.rules) {
        this.gmail = { ...this.gmail, rules: normalizeGmailAttentionRules(patch.gmail.rules) };
      }
      return this.toPublic();
    }, this.config);
  }
}
